import React, { useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import CircledX from "images/sprites/reb/svg/circled-x.svg";
import MessagingIcon from "images/messaging_icon.svg";
import AppointmentIcon from "images/appointment_icon.svg";
import DropdownIcon from "images/ic_clear.svg";
import StylingIcon from "images/ic_styling.svg";
import { flagsAndExperimentNames } from "rtr-constants";
import ModalDrawer from "components/source/shared/modal-drawer/modal-drawer";
import RTRImage from "components/source/shared/rtr-image";
import Modal from "components/source/shared/modal";
import HeapHelpers from "helpers/heap-helpers";
import { getUserId } from "components/source/hoc/with-user-data";
import { isPausedWithItems, isPausedWithoutItems } from "helpers/membership-plan-helpers";
import { useOnLoadFlagOrExperiment } from "components/source/hoc/with-flags-and-experiments";

const propTypes = {
  isModal: PropTypes.bool,
};

const ExpandedModalButtons = ({ icon: Icon, title, description, onClick, isMobile }) => (
  <div className={`flex ${isMobile ? "items-center flex-col" : "items-start"} w-full`}>
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer hover:bg-gray-100 rounded transition p-0 bg-transparent w-full text-left"
      tabIndex={0}>
      {isMobile ? (
        <>
          <div className="flex items-center justify-center mb-1">
            <Icon className="w-6 h-6 mr-2" />
            <p className="font-semibold font-lg m-0 leading-tight">{title}</p>
          </div>
          <div className="text-center">
            <p className="m-0 leading-tight">{description}</p>
          </div>
        </>
      ) : (
        <div className="flex">
          <div className="w-8 flex flex-col items-center">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="font-semibold font-lg m-0 leading-tight mb-1">{title}</p>
            <p className="m-0 leading-tight">{description}</p>
          </div>
        </div>
      )}
    </button>
  </div>
);

const ContactStylistModal = props => {
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const [contactStylistABTestFlag] = useOnLoadFlagOrExperiment(
    flagsAndExperimentNames.SWAT_868_CONTACT_STYLIST_COPY_IMAGE_AB_TEST
  );
  const [contactStylistTermFlag] = useOnLoadFlagOrExperiment(
    flagsAndExperimentNames.SWAT_868_CONTACT_STYLIST_FEATURE_TOGGLE
  );
  const product = useSelector(state => state.product);
  const staticProduct = useSelector(state => state.staticProduct);
  const [visible, setVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showContactStylist, setShowContactStylist] = useState(false);
  const [openPDPModal, setOpenPDPModal] = useState(false);
  const imgSrc = contactStylistABTestFlag ? "/images/ContactStylist_testA.jpg" : "/images/ContactStylist_testB.jpg";
  const ctaCopy = contactStylistABTestFlag ? "Chat with a Stylist" : "Help me Pick";

  const userData = useSelector(state => state.userData);
  const membershipState = useSelector(state => state.membershipState);
  const membershipTerm = membershipState?.membershipTermNumber;

  const isLoggedIn = !!getUserId(userData);
  const membershipTierId = userData?.memberships?.membershipState?.membershipTierId;

  const shouldRenderContactStylist = () => {
    if (!isLoggedIn || membershipTerm > contactStylistTermFlag || contactStylistTermFlag < 0) {
      return null;
    }
    if (!product && staticProduct) {
      return membershipTierId >= 0 && membershipTierId <= 2;
    }
    if (!product && !staticProduct) {
      return null;
    }

    if (isPausedWithItems(membershipState) || isPausedWithoutItems(membershipState)) {
      return null;
    }

    return true;
  };

  const heapData = {
    ImageTitle: imgSrc.includes("testA") ? "Get complimentary personal styling" : "Want help finding styles?",
    ctaCopy: ctaCopy,
    isPDP: props.isModal,
  };

  const handleDismiss = () => {
    setShowContactStylist(false);
    if (!props.isModal && !isMobileViewport) {
      setVisible(false);
    }
    HeapHelpers.fireHeapEvent("contact_stylist_close_event", { heapData });
  };

  const handleMobileButtonClick = () => {
    HeapHelpers.fireHeapEvent("contact_stylist_mobile_cta_click", { heapData });
    setShowContactStylist(true);
    setIsExpanded(true);
  };

  const handleMobileSMS = () => {
    if (!isMobileViewport) return;
    window.open("sms:69787?body=STYLE", "_blank");
    HeapHelpers.fireHeapEvent("contact_stylist_sms_click", { heapData });
  };

  const modalOptions = [
    {
      icon: MessagingIcon,
      title: "Text a stylist",
      description: "Text STYLE to 697-87 to book an appointment",
      onClick: () => {
        handleMobileSMS();
      },
    },
    {
      icon: AppointmentIcon,
      title: "Book an appointment",
      description: "Meet with your stylist live via Zoom",
      onClick: () => {
        HeapHelpers.fireHeapEvent("contact_stylist_book_appointment_click", { heapData });
        window.open("https://book.squareup.com/appointments/7pd5vn2bloge04/location/LSDE2KDN7VQR1/services", "_blank");
      },
    },
  ];

  const expandedModal = () => (
    <div className="w-full bg-white p-6 rounded-xl shadow-lg">
      {modalOptions.map((option, index) => (
        <div key={option.title}>
          <ExpandedModalButtons
            icon={option.icon}
            title={option.title}
            description={option.description}
            onClick={option.onClick}
            isMobile={isMobileViewport}
          />
          {index < modalOptions.length - 1 && <hr className="my-4 border-t border-lighterGray" />}
        </div>
      ))}
    </div>
  );

  const contactStylist = () => {
    HeapHelpers.fireHeapEvent("contact_stylist_view_event", { heapData });
    return (
      <div className="flex flex-col items-center relative w-full">
        <div className="relative w-full">
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className={`w-full h-full p-0 bg-transparent border-0 cursor-pointer ${
              isExpanded ? "rounded-t-lg rounded-b-none" : "rounded-xl"
            }`}
            aria-label="Expand contact stylist"
            style={{ marginBottom: 0 }}>
            <RTRImage
              src={imgSrc}
              alt="Concierge Icon"
              className={`w-full h-full cursor-pointer ${isExpanded ? "rounded-t-xl rounded-b-none" : "rounded-xl"}`}
              draggable={false}
              style={{ marginBottom: 0, display: "block" }}
            />
          </button>
          {!isMobileViewport &&
            (isExpanded && !props.isModal ? (
              <DropdownIcon
                onClick={() => setIsExpanded(false)}
                data-test-id="condense-button"
                className="absolute right-3 top-3 cursor-pointer w-8 h-8"
              />
            ) : (
              <CircledX
                onClick={handleDismiss}
                data-test-id="close-button"
                className="absolute right-3 top-3 cursor-pointer w-6 h-6"
              />
            ))}
        </div>
        {isExpanded && <div className="w-full">{expandedModal()}</div>}
      </div>
    );
  };

  const mobileContactStylist = () => {
    HeapHelpers.fireHeapEvent("contact_stylist_view_mobile_cta", { heapData });
    return (
      <div className="flex flex-col items-end relative w-full">
        <div
          className="relative w-full flex justify-end"
          role="button"
          tabIndex={0}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
            }
          }}>
          <button
            className="rounded-full bg-white shadow-lg p-3 flex items-center justify-center gap-2"
            onClick={handleMobileButtonClick}>
            <StylingIcon className="w-4 h-4" />
            <span className="whitespace-nowrap">{ctaCopy}</span>
          </button>
          {showContactStylist && (
            <ModalDrawer
              isOpen={true}
              peekHeight="100vh"
              hideDragHandle={true}
              handleClose={() => {
                setShowContactStylist(false);
                HeapHelpers.fireHeapEvent("contact_stylist_drawer_close_event", { heapData });
              }}>
              {contactStylist()}
            </ModalDrawer>
          )}
        </div>
      </div>
    );
  };

  const pdpModal = () => {
    HeapHelpers.fireHeapEvent("contact_stylist_view_pdp_modal_cta", { heapData });
    return (
      <div className="flex flex-col items-center relative w-full">
        <div
          className="relative w-full flex justify-end"
          role="button"
          tabIndex={0}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
            }
          }}>
          <button
            className="rounded-full bg-white shadow-lg p-3 flex items-center gap-2"
            onClick={() => {
              setOpenPDPModal(true);
              setIsExpanded(true);
              setShowContactStylist(true);
              HeapHelpers.fireHeapEvent("contact_stylist_pdp_cta_click", { heapData });
            }}>
            <StylingIcon className="w-4 h-4" />
            <span className="whitespace-nowrap">{ctaCopy}</span>
          </button>
          {showContactStylist && (
            <Modal
              additionalStyles={{
                content: {
                  top: "45%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  padding: 0,
                  borderRadius: "1rem",
                  overflow: "hidden",
                },
              }}
              className="w-[375px] fixed inset-0 flex items-center rounded-xl"
              data-test-id="contact-stylist-modal"
              isOpen={openPDPModal}
              ariaHideApp={false}>
              {contactStylist()}
            </Modal>
          )}
        </div>
      </div>
    );
  };

  if (!visible || !shouldRenderContactStylist()) return null;

  return isMobileViewport ? mobileContactStylist() : props.isModal ? pdpModal() : contactStylist();
};

ExpandedModalButtons.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  isMobile: PropTypes.bool,
};

ContactStylistModal.propTypes = propTypes;
ContactStylistModal.defaultProps = {
  isModal: false,
};

export default ContactStylistModal;
